import React, { useEffect, useMemo, useState } from 'react';
import { X, Upload, Loader2, ImageIcon, Sparkles } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import {
  EnrichmentDetail,
  ProductEnrichment,
} from '../../services/distributorService';
import {
  PublishDomain,
  publishEnrichmentToCatalogue,
  suggestCatalogueProductName,
  suggestDomain,
} from './publishEnrichment';

const DEFAULT_DISTRIBUTOR_MARKUP_PERCENT = 18;

/**
 * Modal wizard for publishing an approved enrichment to the partner's
 * Products catalogue. Replaces the slim inline popover that only let
 * the partner pick a domain — adds sell-price / markup editing and an
 * image-URL override so the partner can finalise the listing in one
 * step instead of bouncing into Products afterwards.
 *
 * Pricing math:
 *  - Distributor price (buy) is read-only.
 *  - Markup % and Sell price are bidirectionally linked — typing in
 *    one updates the other so partners can think in either dimension.
 *
 * Image override is a free-text URL with a live preview underneath.
 * Empty falls back to the enrichment.imageUrl if set, else "—".
 *
 * Submit calls publishEnrichmentToCatalogue, which idempotently writes
 * to the All-Products snapshot. Success closes the modal and the parent
 * shows a banner.
 */
interface PublishWizardProps {
  open: boolean;
  detail: EnrichmentDetail;
  enrichment: ProductEnrichment;
  distributorLabel: string;
  onClose: () => void;
  onSuccess: (result: { sku: string; updated: boolean }) => void;
  onError: (message: string) => void;
}

export function PublishWizard({
  open,
  detail,
  enrichment,
  distributorLabel,
  onClose,
  onSuccess,
  onError,
}: PublishWizardProps) {
  const [domain, setDomain] = useState<PublishDomain>(() =>
    suggestDomain(enrichment.category?.primary ?? null),
  );
  // Buy price is fixed (live distributor price). Sell price + markup are
  // linked — last-edited drives the other.
  const buyPrice = detail.priceAndStock?.price ?? 0;
  const suggestedName = useMemo(
    () => suggestCatalogueProductName(detail, enrichment, detail.sku),
    [detail, enrichment],
  );
  const defaultSellPrice = useMemo(
    () => Math.round(buyPrice * (1 + DEFAULT_DISTRIBUTOR_MARKUP_PERCENT / 100) * 100) / 100,
    [buyPrice],
  );
  const [sellPrice, setSellPrice] = useState<string>(defaultSellPrice.toFixed(2));
  const [markupPercent, setMarkupPercent] = useState<string>(String(DEFAULT_DISTRIBUTOR_MARKUP_PERCENT));
  const [name, setName] = useState<string>(suggestedName);
  const [imageUrl, setImageUrl] = useState<string>(enrichment.imageUrl ?? '');
  const [submitting, setSubmitting] = useState(false);

  // Reset whenever the modal is re-opened on a different SKU.
  useEffect(() => {
    if (!open) return;
    setDomain(suggestDomain(enrichment.category?.primary ?? null));
    setSellPrice(defaultSellPrice.toFixed(2));
    setMarkupPercent(String(DEFAULT_DISTRIBUTOR_MARKUP_PERCENT));
    setName(suggestedName);
    setImageUrl(enrichment.imageUrl ?? '');
  }, [open, detail.sku, enrichment.imageUrl, enrichment.category?.primary, defaultSellPrice, suggestedName]);

  const onSellChange = (value: string) => {
    setSellPrice(value);
    const parsed = parseFloat(value);
    if (!isNaN(parsed) && buyPrice > 0) {
      const pct = ((parsed / buyPrice) - 1) * 100;
      setMarkupPercent(pct.toFixed(1));
    }
  };
  const onMarkupChange = (value: string) => {
    setMarkupPercent(value);
    const parsed = parseFloat(value);
    if (!isNaN(parsed)) {
      const next = Math.round(buyPrice * (1 + parsed / 100) * 100) / 100;
      setSellPrice(next.toFixed(2));
    }
  };

  const margin = useMemo(() => {
    const sell = parseFloat(sellPrice);
    if (isNaN(sell) || sell <= 0) return null;
    const grossMargin = sell - buyPrice;
    const grossMarginPct = (grossMargin / sell) * 100;
    return { dollars: grossMargin, pct: grossMarginPct };
  }, [sellPrice, buyPrice]);

  const handleSubmit = async () => {
    const sellPriceNum = parseFloat(sellPrice);
    if (isNaN(sellPriceNum) || sellPriceNum < 0) {
      onError('Sell price must be a positive number');
      return;
    }
    if (!name.trim()) {
      onError('Product name is required');
      return;
    }
    setSubmitting(true);
    try {
      const result = await publishEnrichmentToCatalogue(detail, enrichment, {
        source: 'ingram',
        sourceLabel: distributorLabel,
        sku: detail.sku,
        domain,
        sellPriceOverride: sellPriceNum,
        markupPercent: parseFloat(markupPercent) || 0,
        imageUrlOverride: imageUrl.trim() || null,
        nameOverride: name.trim(),
      });
      onSuccess({ sku: detail.sku, updated: result.updated });
    } catch (err: any) {
      onError(err?.message ?? 'Publish failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-[1px]" />
      <div
        className="relative bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto flex flex-col"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Publish to catalogue"
      >
        {/* Header */}
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between flex-shrink-0">
          <div className="inline-flex items-center gap-2.5">
            <div className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-blue-100 text-blue-700">
              <Upload className="h-4 w-4" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-slate-900 m-0">Publish to catalogue</h3>
              <p className="text-xs text-slate-500 m-0 mt-0.5">
                Add this {distributorLabel} SKU to your Products list
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="inline-flex items-center justify-center h-8 w-8 rounded-md text-slate-500 hover:bg-slate-100"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-auto px-5 py-4 space-y-5">
          {/* Hero strip — image preview + meta */}
          <div className="flex items-start gap-3">
            <ImagePreview url={imageUrl || enrichment.imageUrl || null} />
            <div className="flex-1 min-w-0">
              <div className="text-xs text-slate-500 inline-flex items-center gap-2">
                <span className="font-mono">{detail.sku}</span>
                {enrichment.category?.primary && (
                  <span className="text-slate-300">·</span>
                )}
                {enrichment.category?.primary && (
                  <span>{enrichment.category.primary}</span>
                )}
              </div>
              <div className="text-sm font-medium text-slate-900 mt-1 leading-tight">
                {name || enrichment.canonicalName || detail.sku}
              </div>
              <div className="inline-flex items-center gap-1 text-[10px] text-orange-700 bg-orange-50 ring-1 ring-inset ring-orange-200 rounded-full px-2 py-0.5 mt-2">
                <Sparkles className="h-3 w-3" />
                AI-enriched
              </div>
            </div>
          </div>

          {/* Name */}
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">Product name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={submitting}
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>

          {/* Domain */}
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">
              Domain <span className="text-slate-400 font-normal">— how customers will find this</span>
            </label>
            <select
              value={domain}
              onChange={(e) => setDomain(e.target.value as PublishDomain)}
              disabled={submitting}
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option value="computers">Computers</option>
              <option value="networking">Networking</option>
              <option value="unified-collaboration">Unified Collaboration</option>
              <option value="software">Software</option>
            </select>
            <p className="text-[11px] text-slate-500 mt-1">
              Pre-filled from the enrichment's category — confirm or change before publishing.
            </p>
          </div>

          {/* Pricing */}
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 space-y-3">
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              Pricing
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-[11px] font-medium text-slate-600 mb-1">
                  Buy price
                </label>
                <div className="px-3 py-2 rounded-md bg-white border border-slate-200 text-sm text-slate-900 tabular-nums font-medium">
                  ${buyPrice.toFixed(2)}
                </div>
                <p className="text-[10px] text-slate-400 mt-0.5">From {distributorLabel}</p>
              </div>
              <div>
                <label className="block text-[11px] font-medium text-slate-600 mb-1">
                  Markup %
                </label>
                <input
                  type="number"
                  step="0.1"
                  value={markupPercent}
                  onChange={(e) => onMarkupChange(e.target.value)}
                  disabled={submitting}
                  className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
                <p className="text-[10px] text-slate-400 mt-0.5">Drives sell price</p>
              </div>
              <div>
                <label className="block text-[11px] font-medium text-slate-600 mb-1">
                  Sell price
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={sellPrice}
                  onChange={(e) => onSellChange(e.target.value)}
                  disabled={submitting}
                  className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
                <p className="text-[10px] text-slate-400 mt-0.5">What customers pay</p>
              </div>
            </div>
            {margin && margin.dollars > 0 && (
              <div className="text-[11px] text-emerald-700 bg-emerald-50 rounded-md px-2 py-1.5 inline-block">
                Margin: <span className="font-semibold tabular-nums">${margin.dollars.toFixed(2)}</span>
                {' '}({margin.pct.toFixed(1)}%)
              </div>
            )}
            {margin && margin.dollars < 0 && (
              <div className="text-[11px] text-rose-700 bg-rose-50 rounded-md px-2 py-1.5 inline-block">
                Loss: <span className="font-semibold tabular-nums">${Math.abs(margin.dollars).toFixed(2)}</span>
                {' '}— sell price is below buy.
              </div>
            )}
          </div>

          {/* Image */}
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">
              Image URL <span className="text-slate-400 font-normal">(optional)</span>
            </label>
            <input
              type="url"
              placeholder="https://… (paste a product image URL)"
              value={imageUrl}
              onChange={(e) => setImageUrl(e.target.value)}
              disabled={submitting}
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            <p className="text-[11px] text-slate-500 mt-1">
              Leave blank to use the enrichment image{enrichment.imageUrl ? ' (set)' : ' (none yet)'}.
              Paste a URL to override.
            </p>
          </div>

          <div className="text-[11px] text-slate-500">
            Subsequent syncs will keep buy-price + stock fresh on this entry.
            Edit the rest in <span className="font-medium text-slate-700">Marketplace → Products</span> after publish.
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-slate-100 flex items-center justify-end gap-2 flex-shrink-0">
          <Button type="button" variant="outline" onClick={onClose} disabled={submitting} className="inline-flex">
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={submitting}
            className="inline-flex bg-blue-600 hover:bg-blue-700 text-white"
          >
            {submitting ? (
              <>
                <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                Publishing…
              </>
            ) : (
              <>
                <Upload className="h-3.5 w-3.5 mr-1" />
                Publish to catalogue
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}

function ImagePreview({ url }: { url: string | null }) {
  const [errored, setErrored] = useState(false);
  if (url && !errored) {
    return (
      <img
        src={url}
        alt=""
        onError={() => setErrored(true)}
        className="h-20 w-20 rounded-lg border border-slate-200 object-contain bg-white flex-shrink-0"
      />
    );
  }
  return (
    <div className="h-20 w-20 rounded-lg border border-dashed border-slate-300 bg-slate-50 inline-flex items-center justify-center text-slate-400 flex-shrink-0">
      <ImageIcon className="h-6 w-6" />
    </div>
  );
}
